import {
  IconAlertTriangle,
  IconCircle,
  IconCircleCheck,
  IconCube,
  IconExclamationCircle,
  IconStack2,
} from "@tabler/icons-react";
import { Row } from "@tanstack/react-table";
import {
  ObjectUpdateData,
  OperationType,
  ProductType,
  SingleItem,
} from "api-client";
import { useTranslation } from "react-i18next";
import { MEStore } from "features/MappingEditor/MappingEditorStore";

import { useParams } from "react-router";
import useToast from "hooks/useToast";
import Alert from "components/Alert";
import { buildingStore } from "store/Building/BuildingStore";
import { dynamicTableStore } from "../DynamicTable/DynamicTableStore.ts";

const useMappingEditor = () => {
  const { t } = useTranslation();
  const { ifc_id } = useParams();
  const toast = useToast();

  const checkRow = (row?: Row<unknown>) => {
    if (!row) return;
    const selectedIndexes = [
      ...MEStore.selectedRowIndexes,
      String(row?.index),
    ].sort((a, b) => a.localeCompare(b));
    MEStore.setSelectedRows(Array.from(new Set(selectedIndexes)));
  };

  const unCheckRow = (row?: Row<unknown>) => {
    if (!row) return;
    const selectedIndexes = MEStore.selectedRowIndexes.filter(
      (item) => item != String(row.index)
    );
    MEStore.setSelectedRows(selectedIndexes);
  };

  const contextMenuOptions = [
    {
      title: t("mappingEditor.checkProductLink"),
      options: [
        {
          label: t("mappingEditor.markAsChecked"),
          onClick: checkRow,
          icon: <IconCircleCheck size={18} />,
        },
        {
          label: t("mappingEditor.markAsNotChecked"),
          onClick: unCheckRow,
          icon: <IconCircle size={18} />,
        },
      ],
    },
  ];

  function save3DViewerState(open: boolean) {
    const currentState = get3DViewerState();
    currentState[String(ifc_id)] = open;
    localStorage.setItem("3D_viewer_state", JSON.stringify(currentState));
  }

  function get3DViewerState() {
    return JSON.parse(localStorage.getItem("3D_viewer_state") ?? "{}");
  }

  const checkConfirmation = async () => {
    if (dynamicTableStore.filterStore.validFilterRules?.rules.length) {
      return true;
    } else {
      const isConfirmed = await MEStore.showConfirmation();
      return isConfirmed;
    }
  };

  function showRulesToastMessage(affectedObjectsCount: number) {
    if (affectedObjectsCount === 0) {
      toast(t("mappingEditor.noRuleBasedMapping"), "attention");
    } else if (affectedObjectsCount === 1) {
      toast(t("mappingEditor.ruleBasedMappingSuccessOne"), "success");
    } else {
      toast(
        t("mappingEditor.ruleBasedMappingSuccess", {
          count: affectedObjectsCount,
        }),
        "success"
      );
    }
  }

  function showUpdatedToastMessage(count: number) {
    if (count === 0) {
      toast(t("mappingEditor.noUpdateMappingSuccess"), "attention");
    } else if (count === 1) {
      toast(t("mappingEditor.updateMappingSuccessOne"), "success");
    } else {
      toast(
        t("mappingEditor.updateMappingSuccess", {
          count: count,
        }),
        "success"
      );
    }
  }

  async function mappingToFile(
    operation_type: OperationType,
    product_id?: string | null,
    object_update_data?: ObjectUpdateData | null
  ) {
    const confirmed = await checkConfirmation();
    if (confirmed) {
      try {
        const response = await MEStore.runFileOperation(
          toast,
          ifc_id,
          operation_type,
          product_id,
          object_update_data,
          true
        );
        const metaData = response?.metadata;
        if (operation_type === OperationType.RuleBasedMapping) {
          showRulesToastMessage(
            Number(metaData?.number_of_affected_objects) || 0
          );
        }
        if (operation_type === OperationType.UpdateOutdatedMappings) {
          if (metaData?.outdated_components_names?.length) {
            MEStore.setOpenOutdatedComponentsPopup({
              open: true,
              items: metaData.outdated_components_names,
              count: Number(metaData.number_of_affected_objects),
            });
          } else {
            showUpdatedToastMessage(
              Number(metaData?.number_of_affected_objects)
            );
          }
        }
      } catch (e) {
        console.error(e);
      }
      return true;
    } else {
      return false;
    }
  }

  async function mappingToObjects(
    operation_type: OperationType,
    produc_id?: string | null,
    current_object?: SingleItem | null,
    object_update_data?: ObjectUpdateData | null
  ) {
    const response = await MEStore.runSelectedObjectOperations(
      toast,
      ifc_id,
      operation_type,
      produc_id,
      current_object,
      object_update_data
    );
    const metaData = response?.metadata;
    if (operation_type === OperationType.UpdateOutdatedMappings) {
      if (metaData?.outdated_components_names?.length) {
        MEStore.setOpenOutdatedComponentsPopup({
          open: true,
          items: metaData.outdated_components_names,
          count: Number(metaData.number_of_affected_objects),
        });
      } else {
        showUpdatedToastMessage(
          Number(response?.metadata?.number_of_affected_objects)
        );
      }
    }
  }

  function getMappingChipIcon(
    isCompliant: boolean,
    productType: ProductType | undefined | null | string,
    isOutdated?: boolean
  ) {
    if (isOutdated) {
      return (
        <IconAlertTriangle
          stroke={3}
          width={16}
          height={16}
          className="rounded-full text-yellow-600"
        />
      );
    }
    if (isCompliant) {
      return productType === "PRODUCT" ? (
        <IconCube
          stroke={3}
          width={16}
          height={16}
          className="rounded-full text-green-600"
        />
      ) : (
        <IconStack2
          stroke={3}
          width={19}
          height={19}
          className="rounded-full text-green-600"
        />
      );
    }

    if (!isCompliant) {
      return (
        <IconExclamationCircle
          stroke={3}
          width={16}
          height={16}
          className="rounded-full text-red-600"
        />
      );
    }
  }

  function getMappingChipTooltipContent(
    isCompliant: boolean,
    isOutdated?: boolean
  ) {
    const certification = buildingStore.getBuildingCertificationName();

    if (isOutdated) {
      return (
        <Alert
          title={t("mappingEditor.outdatedAlertTitle")}
          description={t("mappingEditor.outdatedAlertDesc")}
          type="attention"
        />
      );
    }
    if (!isCompliant) {
      return (
        <Alert
          title={t("mappingEditor.notCompliantAlertTitle", {
            certification: certification,
          })}
          description={t("mappingEditor.notCompliantAlertDesc", {
            certification: certification,
          })}
          type="warning"
        />
      );
    }
    return "";
  }

  return {
    contextMenuOptions,
    save3DViewerState,
    get3DViewerState,
    mappingToFile,
    mappingToObjects,
    showUpdatedToastMessage,
    getMappingChipTooltipContent,
    getMappingChipIcon,
  };
};

export default useMappingEditor;
